/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { useEcsperAuth } from '@ecsper/auth';
import { readDocumentContext } from '@ecsper/document-context';
import { getCompressedDocumentDocxBlob, subscribeParagraphChanges } from '@ecsper/host-word';
import type { DocumentContext, ToastSeverity } from '@ecsper/speciality-contract';
import { stampDocumentParagraphs } from '@ecsper/speciality-expert-witness';
import type { UUIDv7 } from '@ecsper/types';
import type { SaveState } from '@ecsper/ui-panels';
import { useToast } from '@ecsper/ui-primitives';
import type { ReportVersionSummary } from '@ecsper/vault-client';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AppContext } from '../bootstrap.js';
import { GovernanceBadge } from '../components/GovernanceBadge.js';
import { SaveControls } from '../components/SaveControls.js';
import { mapAuthStateToSession } from '../map-session.js';
import { SidebarPanels } from '../panels/SidebarPanels.js';
import { AutosaveService } from '../services/autosave.js';
import { detectWopiMode } from '../services/wopiMode.js';

export interface AppShellProps {
  readonly ctx: AppContext;
  /** Active report ID from the URL query string (Stage 5.5). */
  readonly reportId: string;
  /** Matter scope from URL or custom XML (Stage 5.5). */
  readonly matterId: string;
}

const SAVED_STATUS_DISPLAY_MS = 3_000;

/**
 * Wires {@link AppContext} to the active speciality task pane: governed autosave,
 * vault versions, paragraph stamping, and CPR compliance UI.
 */
export function AppShell(props: AppShellProps): ReactElement {
  const { ctx, reportId, matterId } = props;
  const { toast } = useToast();
  const { state: authState } = useEcsperAuth();

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | undefined>(undefined);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [versions, setVersions] = useState<readonly ReportVersionSummary[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [highlightVersionNumber, setHighlightVersionNumber] = useState<number | undefined>(
    undefined,
  );
  const [documentContext, setDocumentContext] = useState<DocumentContext>({
    documentId: null,
    isDirty: false,
    hostMetadata: {},
  });

  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reportUuid = reportId as UUIDv7;

  const loadVersions = useCallback(async (): Promise<void> => {
    setIsLoadingVersions(true);
    try {
      const result = await ctx.vaultClient.listVersions(reportUuid);
      setVersions([...result]);
    } catch (e) {
      toast(`Could not load versions: ${String((e as Error).message)}`, 'error');
    } finally {
      setIsLoadingVersions(false);
    }
  }, [ctx.vaultClient, reportUuid, toast]);

  const wopiMode = useMemo(() => detectWopiMode(), []);

  const autosave = useMemo(
    () =>
      new AutosaveService({
        reportId: reportUuid,
        vaultClient: ctx.vaultClient,
        ...(wopiMode.isWopiMode ? { wopiContext: wopiMode } : {}),
        getDocxBlob: () => getCompressedDocumentDocxBlob(),
        onSaveStart: () => {
          if (savedStatusTimerRef.current !== null) {
            clearTimeout(savedStatusTimerRef.current);
            savedStatusTimerRef.current = null;
          }
          setSaveState('saving');
        },
        onSaveComplete: (version) => {
          setSaveState('saved');
          setLastSavedAt(new Date().toISOString());
          setSaveError(undefined);
          setHighlightVersionNumber(version);
          void loadVersions();
          void stampDocumentParagraphs().catch((e) => {
            toast(`Could not refresh paragraph stamps: ${String((e as Error).message)}`, 'error');
          });
          savedStatusTimerRef.current = setTimeout(() => {
            setSaveState('idle');
            savedStatusTimerRef.current = null;
          }, SAVED_STATUS_DISPLAY_MS);
        },
        onSaveError: (error) => {
          setSaveState('error');
          setSaveError(error.message);
        },
        onUnsavedChange: () => {
          setSaveState((current) => (current === 'saving' ? current : 'unsaved'));
        },
      }),
    [ctx.vaultClient, loadVersions, reportUuid, toast, wopiMode],
  );

  useEffect(() => {
    // eslint-disable-next-line no-console -- acceptance criterion: log active save transport at startup
    console.info(`[Ecsper] Save mode: ${wopiMode.isWopiMode ? 'WOPI PutFile' : 'Workspace API'}`);
  }, [wopiMode]);

  useEffect(() => {
    let cancelled = false;
    void readDocumentContext().then((ecsper) => {
      if (cancelled) {
        return;
      }
      setDocumentContext({
        documentId: ecsper?.reportId ?? null,
        isDirty: false,
        hostMetadata: ecsper !== null ? { ecsperDocument: ecsper } : {},
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeParagraphs: (() => void) | undefined;

    void (async () => {
      try {
        await stampDocumentParagraphs();
        if (cancelled) {
          return;
        }
        autosave.start();
        unsubscribeParagraphs = await subscribeParagraphChanges(() => {
          autosave.notifyDocumentChanged();
        });
      } catch (e) {
        if (!cancelled) {
          toast(`Could not initialise document: ${String((e as Error).message)}`, 'error');
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeParagraphs?.();
      autosave.stop();
      if (savedStatusTimerRef.current !== null) {
        clearTimeout(savedStatusTimerRef.current);
      }
    };
  }, [autosave, toast]);

  useEffect(() => {
    void loadVersions();
  }, [loadVersions, reportId]);

  useEffect(() => {
    const onSaveToVault = (): void => {
      void autosave.saveNow();
    };
    window.addEventListener('ecsper:save-to-vault', onSaveToVault);
    return () => {
      window.removeEventListener('ecsper:save-to-vault', onSaveToVault);
    };
  }, [autosave]);

  const session = mapAuthStateToSession(authState);
  if (session === null) {
    return <div role="alert">Session unavailable.</div>;
  }

  const TaskPane = ctx.activeSpeciality.hosts.word?.taskPane;
  if (TaskPane === undefined) {
    return <div role="alert">Active speciality does not provide a Word task pane.</div>;
  }

  const taskPaneProps = {
    documentContext,
    session,
    onRequestSave: async () => {
      await autosave.saveNow();
    },
    onToast: (message: string, severity: ToastSeverity) => {
      toast(message, severity);
    },
    saveControls: (
      <SaveControls
        saveState={saveState}
        {...(lastSavedAt !== undefined ? { lastSavedAt } : {})}
        {...(saveError !== undefined ? { saveError } : {})}
        onSaveNow={() => {
          void autosave.saveNow();
        }}
        onSaveMajorVersion={(note) => {
          void autosave.saveMajorVersion(note);
        }}
        onRetrySave={() => {
          void autosave.saveNow();
        }}
      />
    ),
    governanceBadge: <GovernanceBadge protectionLevel="standard" />,
    aiPanels: <SidebarPanels reportId={reportId} matterId={matterId} apiClient={ctx.apiClient} />,
    versions,
    isLoadingVersions,
    onRefreshVersions: loadVersions,
    highlightVersionNumber,
    ...(lastSavedAt !== undefined ? { lastSavedAt } : {}),
    ...(saveError !== undefined ? { saveError } : {}),
  };

  return <TaskPane {...taskPaneProps} />;
}
